import {
	Box,
	Button,
	Inline,
	ShadowRoot,
	Spinner,
	Stack,
	Text,
} from "@sembark-travel/ui-base"
import { showSnackbar } from "@sembark-travel/ui-snackbar"
import { useXHR } from "@sembark-travel/xhr"
import { useCallback, useEffect, useRef, useState } from "react"

/**
 * Paste-driven email-signature editor. The user pastes their styled signature
 * into a contenteditable box; the raw HTML is sent to the backend, which is the
 * sanitization authority (App\IntegratedApps\Mailbox\Services\HtmlToText). Shows
 * a read-only preview with an Edit button once a signature exists; opens the
 * editor directly when there is none yet.
 */
export function EmailSignatureEditor() {
	const xhr = useXHR()
	const editorRef = useRef<HTMLDivElement | null>(null)
	const [loading, setLoading] = useState(true)
	const [saving, setSaving] = useState(false)
	const [html, setHtml] = useState("")
	const [editing, setEditing] = useState(false)

	useEffect(() => {
		let active = true
		xhr
			.get("/profile/email-signature")
			.then((resp) => {
				if (!active) return
				const value = String(resp.data?.email_signature || "")
				setHtml(value)
				// No signature yet → open the editor straight away.
				if (!value) setEditing(true)
			})
			.catch(() => undefined)
			.then(() => {
				if (active) setLoading(false)
			})
		return () => {
			active = false
		}
	}, [xhr])

	// Seed the contenteditable with the current value when the editor mounts.
	const setEditorNode = useCallback(
		(node: HTMLDivElement | null) => {
			editorRef.current = node
			if (node) node.innerHTML = html
		},
		[html]
	)

	async function save() {
		const value = editorRef.current?.innerHTML || ""
		setSaving(true)
		try {
			const resp = await xhr.put("/profile/email-signature", {
				email_signature: value.trim() ? value : null,
			})
			// The backend returns the sanitized signature — reflect it back.
			setHtml(String(resp.data?.email_signature || ""))
			setEditing(false)
			showSnackbar("Email signature saved.")
		} catch (e) {
			window.alert((e as Error).message || "Could not save the signature.")
		} finally {
			setSaving(false)
		}
	}

	if (loading) {
		return <Spinner />
	}

	const heading = (
		<Text fontWeight="semibold" fontSize="lg">
			Email Signature
		</Text>
	)

	if (!editing) {
		return (
			<Stack gap="3">
				<Stack gap="1">
					{heading}
					<Text fontSize="sm" color="muted">
						Added to the emails you send from Sembark.
					</Text>
				</Stack>
				{html ? (
					<Box borderWidth="1" rounded="md" padding="3" overflow="auto">
						<ShadowRoot>
							<div dangerouslySetInnerHTML={{ __html: html }} />
						</ShadowRoot>
					</Box>
				) : (
					<Text color="muted">No signature set.</Text>
				)}
				<Box>
					<Button status="primary" onClick={() => setEditing(true)}>
						{html ? "Edit Signature" : "Add Signature"}
					</Button>
				</Box>
			</Stack>
		)
	}

	return (
		<Stack gap="3">
			<Stack gap="1">
				{heading}
				<Text fontSize="sm" color="muted">
					Paste your signature below — copy it from your existing email client
					to keep the styling.
				</Text>
			</Stack>
			<div
				ref={setEditorNode}
				contentEditable
				suppressContentEditableWarning
				style={{
					minHeight: "120px",
					border: "1px solid #d0d5dd",
					borderRadius: "6px",
					padding: "12px",
					outline: "none",
					maxWidth: "100%",
					overflow: "auto",
				}}
			/>
			<Inline gap="2">
				<Button status="primary" onClick={save} disabled={saving}>
					{saving ? "Saving…" : "Save Signature"}
				</Button>
				<Button onClick={() => setEditing(false)} disabled={saving}>
					Cancel
				</Button>
			</Inline>
		</Stack>
	)
}
